import { useEffect, useState } from "react";
import { Loader2, Save, FileUp, Trash2 } from "lucide-react";
import { toast } from "sonner";
import proApi, { proErr } from "../../lib/proApi";
import RichTextEditor from "../../components/RichTextEditor";
import { downloadPdf } from "../../lib/letterPdf";

const MARGINS = { top: 20, bottom: 20, left: 20, right: 20 };
const b64ToFile = (b64, name) => new File([Uint8Array.from(atob(b64), (c) => c.charCodeAt(0))], name, { type: "application/pdf" });

// Editor relazioni/atti (Medico Legale, Avvocato): modelli dello studio + modelli personali, salvataggio in PDF nella pratica.
export const ProEditor = ({ aid, onSaved }) => {
  const [modelli, setModelli] = useState([]);
  const [titolo, setTitolo] = useState("");
  const [html, setHtml] = useState("");
  const [ver, setVer] = useState(0);
  const [busy, setBusy] = useState(false);
  const [src, setSrc] = useState(null);
  const loadModelli = () => proApi.get("/professionista/modelli").then(({ data }) => setModelli(data)).catch(() => {});
  useEffect(() => { loadModelli(); }, []);

  const usaModello = async (id) => {
    const m = modelli.find((x) => x.id === id);
    if (!m) return;
    try {
      const { data } = await proApi.post(`/professionista/pratiche/${aid}/render`, { template_id: m.id });
      setHtml(data.content); setSrc({ raw: m.content, rendered: data.content }); setTitolo((t) => t || m.title); setVer((v) => v + 1);
    } catch (e) { toast.error(proErr(e)); }
  };
  const salvaModello = async () => {
    const t = window.prompt("Nome del modello personale", titolo || "");
    if (!t) return;
    const content = src && src.rendered === html ? src.raw : html;
    try { await proApi.post("/professionista/modelli", { title: t, content }); toast.success("Modello salvato"); loadModelli(); } catch (e) { toast.error(proErr(e)); }
  };
  const eliminaModello = async (m) => {
    if (!window.confirm(`Eliminare il modello "${m.title}"?`)) return;
    await proApi.delete(`/professionista/modelli/${m.id}`); loadModelli();
  };
  const salvaPdf = async () => {
    if (!titolo.trim()) { toast.error("Inserisci un titolo"); return; }
    setBusy(true);
    try {
      const b64 = await downloadPdf(`${titolo}.pdf`, html, false, null, MARGINS, { returnBase64: true });
      const fd = new FormData();
      fd.append("file", b64ToFile(b64, `${titolo.trim()}.pdf`));
      fd.append("relazione", "1");
      await proApi.post(`/professionista/pratiche/${aid}/upload`, fd);
      toast.success("Documento salvato nella pratica");
      setHtml(""); setTitolo(""); setVer((v) => v + 1); onSaved();
    } catch (e) { toast.error(proErr(e)); } finally { setBusy(false); }
  };
  const mine = modelli.filter((m) => !m.studio);
  return (
    <div className="space-y-3" data-testid="pro-editor">
      <div className="flex flex-wrap gap-2 items-center">
        <select onChange={(e) => { usaModello(e.target.value); e.target.value = ""; }} defaultValue="" data-testid="pro-editor-modello" className="h-10 w-full sm:w-auto rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900">
          <option value="" disabled>Parti da un modello…</option>
          {modelli.filter((m) => m.studio).length > 0 && <optgroup label="Modelli dello studio">{modelli.filter((m) => m.studio).map((m) => <option key={m.id} value={m.id}>{m.title}</option>)}</optgroup>}
          {mine.length > 0 && <optgroup label="I miei modelli">{mine.map((m) => <option key={m.id} value={m.id}>{m.title}</option>)}</optgroup>}
        </select>
        <input value={titolo} onChange={(e) => setTitolo(e.target.value)} placeholder="Titolo documento (es. Relazione medico-legale)" data-testid="pro-editor-titolo" className="w-full sm:w-auto sm:flex-1 sm:min-w-[220px] h-10 rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900" />
      </div>
      <div className="rounded-lg border border-slate-300 bg-white overflow-x-auto"><RichTextEditor key={ver} value={html} onChange={setHtml} initialMargins={MARGINS} /></div>
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={salvaPdf} disabled={busy || !html} data-testid="pro-editor-salva" className="h-10 px-4 w-full sm:w-auto justify-center rounded-lg bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold inline-flex items-center gap-2 disabled:opacity-50">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />} Salva in PDF nella pratica</button>
        <button type="button" onClick={salvaModello} disabled={!html} data-testid="pro-editor-salva-modello" className="h-10 px-4 w-full sm:w-auto justify-center rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 inline-flex items-center gap-2 disabled:opacity-50"><Save className="h-4 w-4" /> Salva come mio modello</button>
      </div>
      {mine.length > 0 && (
        <div className="text-xs text-slate-500 flex flex-wrap gap-2">I miei modelli: {mine.map((m) => (
          <span key={m.id} className="inline-flex items-center gap-1 rounded bg-slate-100 px-2 py-0.5">{m.title}<button type="button" onClick={() => eliminaModello(m)} data-testid={`pro-modello-del-${m.id}`}><Trash2 className="h-3 w-3 text-red-500" /></button></span>
        ))}</div>
      )}
    </div>
  );
};
